import { MaximalAmountGeneralEnquiry } from '@/constants'
import {
  getAccosBarMockData,
  getAccosCountMockData,
  getFilterMockData,
  getGeneralEnquiryAccosMockData,
  getListAccosMockData,
  getPackageMockData,
  getRoomMockData,
  getSearchMockData
} from '@/mocks/data'
import { CULTURE, PID } from './helpers'
import type { IAccosApiResponse, IAccosBarDto, IListAccosApiResponse, IRequestOptions, ISaveEnquiryResponse } from './interfaces/apiInterfaces'
import type { IAccosFilter, IEnquiryDto, IFiltersDataInterface, IFilterStoreInterface, IPackageDto, IRoomDto } from './interfaces/appInterfaces'

const development = process.env.NODE_ENV === 'development'

export async function useAccosBarApi(): Promise<{ data: IAccosBarDto[] | null; error: unknown }> {
  const path = '/accosbar/getlocationdto'
  try {
    const res = await buildRequest({ path: path })
    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getAccosBarMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as IAccosBarDto[],
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getAccosBarMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

export async function useFilterApi(): Promise<{ data: IFiltersDataInterface | null; error: unknown }> {
  const path = '/filterpage/getfilters'
  try {
    const res = await buildRequest({ path: path })
    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getFilterMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as IFiltersDataInterface,
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getFilterMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

export async function useSearchApi(
  filter: IFilterStoreInterface,
  from: number = 0,
  take: number = 10,
  seed: number
): Promise<{ data: IAccosApiResponse | null; error: unknown }> {
  const path = '/accos/get'
  try {
    const res = await buildRequest({
      path: path,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { filter, from, take, seed }
    })
    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getSearchMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as IAccosApiResponse,
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getSearchMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

// export async function useSearchMapApi(filter: IFilterStoreInterface, from: number = 0, take: number = 10, seed = Math.random() * 100000): Promise<any> {
//   try {
//     const res = await buildRequest({
//       path: '/accos/get-maps-data',
//       basePathname: '/api',
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: { filter, from, take, seed }
//     })

//     if (!res.ok) throw new Error(`a error happend while get filters. status:${res.status}`)
//     return res.json()
//   } catch (error) {
//     return {
//       data: null,
//       error: error
//     }
//   }
// }

export async function useListAccosApi(companyIds: number[]): Promise<{ data: IListAccosApiResponse | null; error: unknown }> {
  const path = '/accos/getbycompanyids'
  try {
    const res = await buildRequest({
      path: path,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { companyIds }
    })

    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getListAccosMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as IListAccosApiResponse,
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getListAccosMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

export async function useGeneralEnquiryAccosApi(
  filter: IAccosFilter,
  maximalAmount: number = MaximalAmountGeneralEnquiry
): Promise<{ data: IListAccosApiResponse | null; error: unknown }> {
  const path = '/accos/getforgeneralenquiry'
  try {
    const res = await buildRequest({
      path: path,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { filter, maximalAmount }
    })

    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getGeneralEnquiryAccosMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as IListAccosApiResponse,
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getGeneralEnquiryAccosMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

export async function useAccosCountApi(
  filter: IFilterStoreInterface,
  from: number = 0,
  take: number = 0,
  seed: number = 0
): Promise<{ data: number | null; error: unknown }> {
  const path = '/accos/count'
  try {
    const res = await buildRequest({
      path: path,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { filter, from, take, seed }
    })

    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getAccosCountMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as number,
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getAccosCountMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

export async function useRoomApi(roomId: number): Promise<{ data: IRoomDto | null; error: unknown }> {
  const path = '/enquiryapi/getroomdata'
  try {
    const res = await buildRequest({
      path: path,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { roomId }
    })

    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getRoomMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as IRoomDto,
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getRoomMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

export async function usePackageApi(packageId: number): Promise<{ data: IPackageDto | null; error: unknown }> {
  const path = '/enquiryapi/getpackagedata'
  try {
    const res = await buildRequest({
      path: path,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { packageId }
    })

    if (!res.ok) {
      if (development) {
        console.debug('returned mocked data for ' + path)
        return getPackageMockData()
      } else {
        throw new Error(`An error happene d while fetching. Status: ${res.status}`)
      }
    }
    return {
      data: (await res.json()) as IPackageDto,
      error: null
    }
  } catch (error) {
    if (development) {
      console.debug('returned mocked data for ' + path)
      return getPackageMockData()
    } else {
      return {
        data: null,
        error: error
      }
    }
  }
}

export async function useSaveEnquiryApi(enquiryData: IEnquiryDto): Promise<ISaveEnquiryResponse> {
  try {
    const res = await buildRequest({
      path: '/enquiryapi/save',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: enquiryData
    })

    if (!res.ok) throw new Error(`An error occurred while saving enquiry. Status: ${res.status}`)
    return await res.json()
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unknown error occurred',
      stackTrace: error instanceof Error ? error.stack : undefined
    }
  }
}

function buildRequest(options: IRequestOptions): Promise<Response> {
  options.queryParams = options.queryParams ? { ...options.queryParams, portalId: PID, l: CULTURE } : { portalId: PID, l: CULTURE }
  return fetch(
    `${apiUrl(options.path, options.basePathname, options.baseUrlOverride)}${options.method === 'GET' || !options.method ? '?' + new URLSearchParams(options.queryParams).toString() : ''}`,
    {
      body: JSON.stringify(options.body),
      method: options.method || 'GET',
      headers: options.headers
    }
  )
}

function apiUrl(path: string, basePathName: string = '/api/portalui', baseUrlOvveride?: string): string {
  let baseUrlDev = null
  if (process.env.NODE_ENV === 'development') {
    switch (PID) {
      case 4:
        baseUrlDev = 'https://dev.suedtirol.com'
        break
      case 7:
        baseUrlDev = 'https://dev.garda-see.com'
        break
      case 13:
        baseUrlDev = 'https://dev.dolomites.com'
        break
      default:
        throw new Error('Wrong api domain')
    }
  }
  return `${baseUrlOvveride || baseUrlDev || ''}${basePathName || ''}${path}`
}
